import React, { Component, createRef } from "react";
import {
  HandlerGetRequest,
  HandlerPostRequest,
} from "../../../helpers/Axios";
import ChatInput from "./ChatInput";
import MessageList from "./MessageList";
import ConfirmActionModal from "./ConfirmActionModal";

class FloatingChatWidget extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      messages: [],
      conversations: [],
      currentConversationId: null,
      isLoading: false,
      suggestions: [],
      showActionModal: false,
      pendingAction: null,
      initialized: false,
    };
    this.messagesEndRef = createRef();
    this.chatInputRef = createRef();
    this.debounceTimer = null;
    this.pendingTexts = [];
  }

  componentDidMount() {
    document.addEventListener("ai-copilot-send", this.handleCustomEvent);
  }

  componentWillUnmount() {
    document.removeEventListener("ai-copilot-send", this.handleCustomEvent);
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
  }

  handleCustomEvent = (e) => {
    if (e.detail && e.detail.text) {
      this.setState({ isOpen: true }, () => {
        this.initIfNeeded();
        this.handleSendMessage(e.detail.text);
      });
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevState.messages.length !== this.state.messages.length) {
      this.scrollToBottom();
    }
  }

  scrollToBottom = () => {
    if (this.messagesEndRef.current) {
      this.messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  focusInput = () => {
    setTimeout(() => {
      if (this.chatInputRef.current) this.chatInputRef.current.focus();
    }, 50);
  };

  toggleChat = () => {
    this.setState(
      (prev) => ({ isOpen: !prev.isOpen }),
      () => {
        if (this.state.isOpen) {
          this.initIfNeeded();
          this.focusInput();
        }
      }
    );
  };

  initIfNeeded = () => {
    if (!this.state.initialized) {
      this.loadSuggestions();
      this.loadConversations();
      this.setState({ initialized: true });
    }
  };

  loadSuggestions = async () => {
    try {
      const suggestions = await HandlerGetRequest({
        path: "/api/ai/copilot/suggestions",
      });
      this.setState({ suggestions });
    } catch (err) {
      console.error("Failed to load suggestions:", err);
    }
  };

  loadConversations = async () => {
    try {
      const conversations = await HandlerGetRequest({
        path: "/api/ai/copilot/conversations",
      });
      this.setState({ conversations });
    } catch (err) {
      console.error("Failed to load conversations:", err);
    }
  };

  loadConversation = async (conversationId) => {
    try {
      const data = await HandlerGetRequest({
        path: `/api/ai/copilot/conversations/${conversationId}`,
      });
      this.setState({
        currentConversationId: conversationId,
        messages: (data.messages || []).map((m) => ({
          id: m.id,
          role: m.role,
          text: m.content_text,
          data: m.content_json,
          timestamp: m.createdAt,
        })),
      });
    } catch (err) {
      console.error("Failed to load conversation:", err);
    }
  };

  handleTyping = () => {
    // Reset debounce timer while user is still typing
    if (this.pendingTexts.length > 0 && this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.flushPendingMessages(), 3000);
    }
  };

  handleSendMessage = (text) => {
    if (!text.trim()) return;

    // Show user message immediately in chat
    const userMsg = {
      id: Date.now(),
      role: "user",
      text,
      timestamp: new Date().toISOString(),
    };
    this.setState((prev) => ({
      messages: [...prev.messages, userMsg],
      isLoading: true,
    }));

    // Buffer the text and reset debounce timer
    this.pendingTexts.push(text.trim());
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.flushPendingMessages(), 3000);
  };

  flushPendingMessages = async () => {
    if (this.pendingTexts.length === 0) return;

    // Combine all buffered texts into one message
    const combinedText = this.pendingTexts.join("\n");
    this.pendingTexts = [];
    this.debounceTimer = null;

    try {
      const result = await HandlerPostRequest({
        path: "/api/ai/copilot/chat",
        body: {
          message: combinedText,
          conversation_id: this.state.currentConversationId,
        },
      });

      const assistantMsg = {
        id: Date.now() + 1,
        role: "assistant",
        text: result.response?.text || "",
        data: result.response,
        timestamp: new Date().toISOString(),
      };

      this.setState((prev) => ({
        messages: [...prev.messages, assistantMsg],
        currentConversationId: result.conversation_id,
        isLoading: false,
      }));

      if (result.response?.type === "action_proposal") {
        this.setState({
          showActionModal: true,
          pendingAction: result.response,
        });
      }
    } catch (err) {
      console.error("Chat error:", err);
      const errorMsg = {
        id: Date.now() + 1,
        role: "assistant",
        text: "Xin lỗi, có lỗi xảy ra. Vui lòng thử lại.",
        data: { type: "error" },
        timestamp: new Date().toISOString(),
      };
      this.setState((prev) => ({
        messages: [...prev.messages, errorMsg],
        isLoading: false,
      }));
    }
  };

  handleActionConfirm = async () => {
    const { pendingAction, currentConversationId } = this.state;
    if (!pendingAction) return;

    try {
      const result = await HandlerPostRequest({
        path: "/api/ai/copilot/actions/execute",
        body: {
          action_type: pendingAction.action_type,
          execute_params: pendingAction.execute_params,
          conversation_id: currentConversationId,
        },
      });

      const successMsg = {
        id: Date.now(),
        role: "assistant",
        text: result.result?.message || "Thực hiện thành công!",
        data: { type: "text", text: result.result?.message },
        timestamp: new Date().toISOString(),
      };

      if (pendingAction.action_type === "send_message" && pendingAction.generated_message) {
        try {
          await navigator.clipboard.writeText(pendingAction.generated_message);
          successMsg.text += "\n(Đã sao chép tin nhắn vào clipboard)";
        } catch (e) {}
      }

      this.setState((prev) => ({
        messages: [...prev.messages, successMsg],
        showActionModal: false,
        pendingAction: null,
      }));
    } catch (err) {
      throw err;
    }
  };

  handleActionCancel = () => {
    const cancelMsg = {
      id: Date.now(),
      role: "assistant",
      text: "Đã hủy hành động.",
      data: { type: "text", text: "Đã hủy hành động." },
      timestamp: new Date().toISOString(),
    };
    this.setState((prev) => ({
      messages: [...prev.messages, cancelMsg],
      showActionModal: false,
      pendingAction: null,
    }));
  };

  handleNewConversation = () => {
    this.setState({
      messages: [],
      currentConversationId: null,
    });
  };

  handleSuggestionClick = (text) => {
    this.handleSendMessage(text);
  };

  render() {
    const {
      isOpen,
      messages,
      isLoading,
      suggestions,
      showActionModal,
      pendingAction,
    } = this.state;

    return (
      <>
        {/* Chat Panel */}
        {isOpen && (
          <div
            onClick={this.focusInput}
            style={{
              position: "fixed",
              bottom: 130,
              right: 10,
              width: 380,
              height: 620,
              zIndex: 1040,
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
              display: "flex",
              flexDirection: "column",
              backgroundColor: "#fff",
            }}
          >
            {/* Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "#fff",
                padding: "10px 14px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 14 }}>
                <i className="iconsminds-brain" style={{ marginRight: 8 }} />
                AI Copilot
              </span>
              <div>
                <button
                  onClick={this.handleNewConversation}
                  title="Cuộc hội thoại mới"
                  style={{
                    background: "none",
                    border: "none",
                    color: "#fff",
                    cursor: "pointer",
                    padding: "2px 6px",
                    fontSize: 14,
                    opacity: 0.8,
                  }}
                >
                  <i className="simple-icon-plus" />
                </button>
                <button
                  onClick={this.toggleChat}
                  title="Đóng"
                  style={{
                    background: "none",
                    border: "none",
                    color: "#fff",
                    cursor: "pointer",
                    padding: "2px 6px",
                    fontSize: 16,
                    opacity: 0.8,
                  }}
                >
                  <i className="simple-icon-close" />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "10px 12px",
                minHeight: 0,
              }}
            >
              {messages.length === 0 && !isLoading ? (
                <div style={{ textAlign: "center", marginTop: 40 }}>
                  <i
                    className="iconsminds-speach-bubble-dialog"
                    style={{ fontSize: 40, color: "#ccc", display: "block", marginBottom: 12 }}
                  />
                  <p style={{ color: "#999", fontSize: 13, marginBottom: 16 }}>
                    Xin chào! Tôi là trợ lý AI của VMASS.
                    <br />
                    Hãy hỏi tôi bất kỳ điều gì!
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 4 }}>
                    {suggestions.slice(0, 4).map((s, i) => (
                      <button
                        key={i}
                        className="btn btn-outline-primary btn-xs"
                        style={{ fontSize: 11, padding: "3px 8px", borderRadius: 12 }}
                        onClick={() => this.handleSuggestionClick(s.text)}
                      >
                        {s.text}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <MessageList messages={messages} isLoading={isLoading} />
              )}
              <div ref={this.messagesEndRef} />
            </div>

            {/* Input */}
            <div style={{ borderTop: "1px solid #eee", padding: "8px 12px", flexShrink: 0 }}>
              <ChatInput
                ref={this.chatInputRef}
                onSend={this.handleSendMessage}
                onTyping={this.handleTyping}
                isLoading={isLoading}
                suggestions={[]}
              />
            </div>
          </div>
        )}

        {/* Toggle Button */}
        <div
          onClick={this.toggleChat}
          title="AI Copilot"
          className="d-none d-md-flex"
          style={{
            position: "fixed",
            bottom: 75,
            right: 10,
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: isOpen ? "#dc2626" : "var(--theme-color-1, #145388)",
            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1041,
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.1)";
            e.currentTarget.style.boxShadow = "0 4px 15px rgba(0,0,0,0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.15)";
          }}
        >
          <i
            className={isOpen ? "simple-icon-close" : "iconsminds-speach-bubble-dialog"}
            style={{ fontSize: isOpen ? 14 : 18, color: "#fff" }}
          />
        </div>

        {/* Action Confirmation Modal */}
        <ConfirmActionModal
          isOpen={showActionModal}
          data={pendingAction}
          onConfirm={this.handleActionConfirm}
          onCancel={this.handleActionCancel}
        />
      </>
    );
  }
}

export default FloatingChatWidget;
